import React, { useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { styled, Box } from '@mui/material'
import Drawer from '@mui/material/Drawer'
import AppBar from '@mui/material/AppBar'
import List from '@mui/material/List'
import IconButton from '@mui/material/IconButton'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import Typography from '@mui/material/Typography'
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction'
import ListItemText from '@mui/material/ListItemText'
import DeleteIcon from '@mui/icons-material/Delete'
import LaunchIcon from '@mui/icons-material/Launch'
import CloseIcon from '@mui/icons-material/Close'
import CircularProgress from '@mui/material/CircularProgress'
import { useDispatch, useSelector } from 'react-redux'
import MessageDialog from '../../components/dialogs/MessageDialog'
import SearchBox from '../../components/form/SearchBox'
import { toggledMappingsOpenAction, openMappings, deleteMappings } from '../../store/pages/mapping'

const DRAWER_WIDTH = 500

const StyledDiv = styled('div')(({
  display: 'flex'
}))

const ListContainer = styled(List)({
  overflowY: 'scroll',
  scrollY: true,
  maxHeight: '800px'
})

const SavedMappingsButton = styled(IconButton)({
  display: 'flex',
  fontSize: '10px'
})

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    marginTop: 65,
    width: DRAWER_WIDTH,
    padding: theme.spacing(0.5)
  }
}))

const StyledLaunchIcon = styled(LaunchIcon)({
  transform: 'rotate(270deg)'
})

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  width: 100,
  marginTop: 75,
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  })
}))

export default function MappingsDrawer () {
  const theme = useTheme()
  const { savedMappings, loadingSavedMapping, isOpen, currentMappings, file, studyType } = useSelector(({
    mapping: { savedMappings, loadingSavedMapping, mappingsDrawerOpen: isOpen, mappings: currentMappings },
    navigation: { file },
    project: { studyType }
  }) => ({
    savedMappings, loadingSavedMapping, isOpen, currentMappings, file, studyType
  }))
  const [filterSavedMappings, setFilterSavedMappings] = useState(undefined)
  const dispatch = useDispatch()
  const [openDialog, setOpenDialog] = React.useState(false)
  const DIALOG_TITLE = 'Notification'
  const DIALOG_TEXT = `The mappings that were loaded contains one or more unique keys fields. These fields are used to define a unique policy
  and therefore should be consistent between all files. The fields has been added to each file.`

  const mappings = savedMappings.filter(x => x.mappings.fileType === file.fileType && x.studyType === studyType)
  const filteredOrSavedMappings = filterSavedMappings === undefined
    ? mappings
    : mappings.filter(x => x.name.toLowerCase().match(filterSavedMappings.toLowerCase()))

  const handleSelectSavedMappings = async (value) => {
    const currentFileMappings = currentMappings.find(x => x.guid === file.guid)
    const mappingsToOpen = savedMappings.find(x => x.guid === value.guid && x.studyType === studyType)
    const newElementsAddedOnLoadedMappings = mappingsToOpen.mappings.columns.filter(x =>
      !currentFileMappings.columns.some(y => y.targetColumnName === x.targetColumnName)
    )

    const isANewPrimary = newElementsAddedOnLoadedMappings.findIndex(x => x.isPrimaryKey && x.required === undefined)
    const otherFilesMappings = currentMappings?.filter(x => x.guid !== file.guid)
    let isANewPrimaryInAnyOftheOtherFiles = false

    if (otherFilesMappings && otherFilesMappings.length > 0) {
      for (const file of otherFilesMappings) {
        const newElementsAddedOnLoadedMappingsToOtherFile = mappingsToOpen.mappings?.columns.filter(x =>
          !file.columns.some(y => y.targetColumnName === x.targetColumnName)
        )
        const isANewPrimaryInTheOtherFile = newElementsAddedOnLoadedMappingsToOtherFile.findIndex(x => x.isPrimaryKey && x.required === undefined)
        if (!isANewPrimaryInAnyOftheOtherFiles) {
          isANewPrimaryInAnyOftheOtherFiles = isANewPrimaryInTheOtherFile !== -1
        }
      }
    }

    if (isANewPrimary !== -1 || isANewPrimaryInAnyOftheOtherFiles) {
      setOpenDialog(true)
    }
    dispatch(openMappings(value.guid))
  }

  return (
    <StyledDiv >
      <StyledAppBar position="fixed">
        <SavedMappingsButton
          color="inherit"
          aria-label="open drawer"
          edge="end"
          onClick={() => dispatch(toggledMappingsOpenAction)}
          size="large">
          { theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon /> }
          Saved Mappings
        </SavedMappingsButton>
      </StyledAppBar>
      <StyledDrawer
        variant="persistent"
        anchor="right"
        open={isOpen}
      >
      {
        loadingSavedMapping
          ? (
          <Box sx={{ p: 2 }}>
            Loading Mappings
            <CircularProgress />
          </Box>
            )
          : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Saved Mappings
              </Typography>
              <IconButton
                edge="end"
                aria-label="close"
                onClick={() => dispatch(toggledMappingsOpenAction)}
                size="large"
              >
                <CloseIcon data-testid="CloseIcon" />
              </IconButton>
            </Box>

            <Box sx={{ maxHeight: 40 }}>
              <SearchBox onChange={(e) => setFilterSavedMappings(e.target.value)} />
            </Box>

            <ListContainer>
                {filteredOrSavedMappings.map((value, index) => (
                  <ListItem key={index}>
                    <ListItemAvatar>
                      <IconButton
                        edge="end"
                        aria-label="open"
                        onClick={() => handleSelectSavedMappings(value)}
                        size="large">
                        <StyledLaunchIcon/>
                      </IconButton>
                    </ListItemAvatar>
                    <ListItemText>
                      {value.name}
                    </ListItemText>
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => dispatch(deleteMappings(value.guid))}
                        size="large">
                        <DeleteIcon data-testid="DeleteIcon" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </ListContainer>
              <MessageDialog
              open={openDialog}
              title={DIALOG_TITLE}
              text={DIALOG_TEXT}
              onClose={() => setOpenDialog(false)}
            />
          </Box>
            )}
      </StyledDrawer>
    </StyledDiv>
  )
}
